import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { EDSBadge } from '@gehc-eds-react/eds-badge';
import { EDSButton } from '@gehc-eds-react/eds-button';
import { S3_JOB_PATH, S3_RETRY_JOB_PATH, S3_CANCEL_JOB_PATH } from '../../constants/apiEndpoints';
import { EDSProgressIndicator } from '@eds2-beta1-react/eds-progress-indicator';
import SpinnerComponent from '../../components/SpinnerComponent/SpinnerComponent';
import BreadcrumbNav from '../../components/BreadCrumbNav/BreadcrumbNav';
import { apiFetch } from '../../utils/apiClient';
import logger from '../../utils/logger';
import { addErrorNotification, addSuccessNotification } from '../../redux/slices/notificationSlice';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectUserEmail } from '../../redux/slices/userSlice';
import '../../styles/variables.css';
 
const token = {
    color: {
        canvas: 'var(--app-color-canvas)',
        danger: 'var(--app-color-danger)',
        dangerTint: 'var(--app-color-danger-tint)',
        textPrimary: 'var(--app-color-text-primary)',
        textSecondary: 'var(--app-color-text-secondary)',
        textMuted: 'var(--app-color-text-muted)',
        surface: 'var(--app-color-surface)',
        surfaceMuted: 'var(--app-color-surface-muted)',
        border: 'var(--app-color-border)'
    },
    font: {
        body: 'var(--app-font-body)',
        mono: 'var(--app-font-mono)'
    },
    radius: {
        lg: 'var(--app-radius-lg)'
    }
};
 
const card = {
    backgroundColor: token.color.surface,
    border: `1px solid ${token.color.border}`,
    borderRadius: token.radius.lg,
    boxShadow: '0 1px 2px rgba(17, 24, 39, 0.04)'
};
 
const cardHeader = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '18px 24px',
    borderBottom: `1px solid ${token.color.border}`
};
 
const formatDate = (value) => {
    if (!value) return '--';
    const formDate = new Date(value);
    if (Number.isNaN(formDate.getTime())) return value;
    return formDate.toLocaleString(undefined, {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
};
 
const SUCCESS = new Set(['complete', 'success', 'succeeded']);
const FAILED = new Set(['failed', 'error']);
const WARNING = new Set(['partial', 'partial_success', 'paused']);
const IN_PROGRESS = new Set(['running', 'in_progress', 'processing', 'active']);
const RETRYABLE = new Set(['failed', 'error', 'partial', 'partial_success']);

const normalizeStatus = (status) => String(status ?? '')
    .trim()
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[\s_-]+/g, '_');

const formatStatusLabel = (status) => {
    const normalizedStatus = normalizeStatus(status);

    if (!normalizedStatus) return String(status ?? '--');
    if (normalizedStatus === 'partial_success') return 'Partial Success';

    return normalizedStatus
        .split('_')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
};

const getJobStatusBadge = (status) => {
    const normalizedStatus = normalizeStatus(status);

    const variant = SUCCESS.has(normalizedStatus)
        ? 'success'
        : FAILED.has(normalizedStatus)
            ? 'alert-high'
            : WARNING.has(normalizedStatus)
                ? 'alert-medium'
                : IN_PROGRESS.has(normalizedStatus)
                    ? 'in-progress'
                    : 'accent-gray';

    return { variant, value: formatStatusLabel(status) };
};
 
const BadgeStatusKeyValuePair = ({ label, value, variant }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
        <EDSBadge
            variant={variant}
            value={String(value)}
            size="medium"
        />
        <span style={{ fontSize: '12px', fontWeight: 600, color: '#55595e', letterSpacing: '0.01em' }}>
            {label}
        </span>
    </div>
);
 
// Maps the real `S3_JOB_PATH({job_id})` response shape ({ audit, live }) into the
// view model this page renders, directly from the API response — no dummy
// fallback data.
const buildJobViewModel = (response) => {
    if (!response || (!response.audit && !response.live)) {
        return {
            id: '--',
            status: '--',
            sourceBucket: '--',
            createdAt: null,
            updatedAt: null,
            createdBy: '--',
            retryCount: 0,
            retryMax: 0,
            total: 0,
            copied: 0,
            failedCount: 0,
            failureReason: '',
            nextSteps: []
        };
    }
 
    const audit = response.audit || {};
    const live = response.live || {};
    const progress = live.progress || {};
    const failureReasons = Array.isArray(live.failure_reasons) ? live.failure_reasons : [];
    const failedCount = progress.failed ?? 0;
    const failureReason = failureReasons
        .map((reason) => reason?.FailureReason || reason?.FailureCode || reason?.failure_reason || reason?.failure_code || reason?.message || reason?.reason || reason?.error || reason?.code || reason?.object_key)
        .filter(Boolean)
        .join(', ');
    const nextSteps = [
        live?.next_steps,
        response?.next_steps,
        audit?.next_steps,
        ...failureReasons.map((reason) => reason?.next_steps)
    ]
        .flatMap((value) => {
            if (Array.isArray(value)) return value;
            if (typeof value === 'string') return [value];
            return [];
        })
        .filter((step) => typeof step === 'string' && step.trim().length > 0);
 
    return {
        id: audit.job_id || '--',
        status: audit.status || '--',
        sourceBucket: audit.source_bucket || '--',
        createdAt: live.creation_time || audit.submitted_at || null,
        updatedAt: live.termination_date || live.creation_time || null,
        createdBy: audit.submitted_by || '--',
        // Not part of this API response yet.
        retryCount: 0,
        retryMax: 0,
        total: progress.total ?? 0,
        copied: progress.succeeded ?? 0,
        failedCount,
        failureReason,
        nextSteps
    };
};
 
const JobDetailsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { jobId } = useParams();
    const dispatch = useAppDispatch();
    const loggedInUserEmail = useAppSelector(selectUserEmail);
 
    const resolvedJobId = jobId || location.state?.job?.id
 
    const [jobResponse, setJobResponse] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
 
    const job = useMemo(
        () => buildJobViewModel(jobResponse),
        [jobResponse, resolvedJobId]
    );
 
    const [isRetryingJob, setIsRetryingJob] = useState(false);
    const [isCancellingJob, setIsCancellingJob] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
 
    const failedTotal = job.failedCount;
    const copiedPct = job.total > 0 ? Math.round((job.copied / job.total) * 100) : 0;
    const failedPct = job.total > 0 ? Math.round((failedTotal / job.total) * 100) : 0;
    const normalizedStatus = normalizeStatus(job.status);
    const isActiveJob = normalizedStatus === 'active';
    const isCompletedJob = SUCCESS.has(normalizedStatus);
    const isRetryableJob = RETRYABLE.has(normalizedStatus);
    //const isRetryInProgress = isRetryingJob;
    const isFailedLikeStatus = normalizedStatus === 'failed' || normalizedStatus === 'error';
    const showFailureReason = isFailedLikeStatus || failedTotal > 0;
    const failureReasonText = job.failureReason || 'Failure reason unavailable';
    const nextSteps = Array.isArray(job.nextSteps) ? job.nextSteps : [];
    const sourceIdFromState = location.state?.sourceId || location.state?.source?.source_id || location.state?.source?.sourceId || location.state?.source?.id || '';
 
    const loadJobDetail = useCallback(async ({ showLoader = false } = {}) => {
        if (showLoader) {
            setIsLoading(true);
        }
        setLoadError('');
        try {
            const response = await apiFetch(S3_JOB_PATH(resolvedJobId), {
                method: 'GET',
                retry: false
            });
            setJobResponse(response);
        } catch (error) {
            setJobResponse(null);
            setLoadError('Failed to load job details.');
            logger.error('Job details load error:', error);
            dispatch(addErrorNotification({ message: 'Failed to load job details. Showing unavailable data as placeholders.' }));
        } finally {
            if (showLoader) {
                setIsLoading(false);
            }
        }
    }, [dispatch, resolvedJobId]);
 
    useEffect(() => {
        let isCancelled = false;
 
        const run = async () => {
            if (isCancelled) return;
            await loadJobDetail({ showLoader: true });
        };
 
        run();
        return () => {
            isCancelled = true;
        };
    }, [loadJobDetail]);
 
    const goToJobs = useCallback(() => {
        if (sourceIdFromState) {
            navigate(`/s3-upload/source/${encodeURIComponent(sourceIdFromState)}`, {
                state: {
                    source: location.state?.source,
                    selectedContract: location.state?.selectedContract
                }
            });
            return;
        }
    }, [location.state, navigate, sourceIdFromState]);
 
    const breadcrumbCurrentPath = job.id
        ? `/job-details/${encodeURIComponent(job.id)}`
        : '/jobs';
 
    const refreshJobDetails = async () => {
        setIsRefreshing(true);
        try {
            await loadJobDetail({ showLoader: false });
        } finally {
            setIsRefreshing(false);
        }
    };
 
    const retryJob = async () => {
        setIsRetryingJob(true);
        try {
            await apiFetch(S3_RETRY_JOB_PATH(job.id), {
                method: 'POST',
                body: JSON.stringify({
                    email: loggedInUserEmail || null
                }),
                retry: false
            });
            dispatch(addSuccessNotification({ message: `Retry started for job "${job.id}".` }));
        } catch (error) {
            dispatch(addErrorNotification({ message: 'Failed to start retry. Please try again.' }));
            logger.error('Retry job error:', error);
        } finally {
            setIsRetryingJob(false);
        }
    };
 
    const cancelJob = async () => {
        setIsCancellingJob(true);
        try {
            await apiFetch(S3_CANCEL_JOB_PATH(job.id), {
                method: 'POST',
                retry: false
            });
            dispatch(addSuccessNotification({ message: `Cancel requested for job "${job.id}".` }));
        } catch (error) {
            dispatch(addErrorNotification({ message: 'Failed to cancel job. Please try again.' }));
            logger.error('Cancel job error:', error);
        } finally {
            setIsCancellingJob(false);
        }
    };
 
    // While the real job detail is still in flight, show a loader instead of
    // rendering dummy/fallback data.
    if (isLoading) {
        return (
            <div style={{ minHeight: '100vh', backgroundColor: token.color.canvas, fontFamily: token.font.body }}>
                <SpinnerComponent isVisible={isLoading} overlay={true} message="Loading job details..." size="medium" />
            </div>
        );
    }
 
    return (
        <div style={{ minHeight: '100vh', backgroundColor: token.color.canvas, fontFamily: token.font.body, display: 'flex' }}>
            <SpinnerComponent isVisible={isRefreshing} overlay={true} message="Refreshing job details..." />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Page content */}
                <div style={{ padding: '28px 32px', maxWidth: '1180px', width: '100%', margin: '0 auto' }}>
                    {/* Breadcrumb */}
                    <div style={{ marginBottom: '10px' }}>
                        <BreadcrumbNav
                            rootLabel="Job List"
                            datasetName={job.id || 'Jobs'}
                            breadcrumb={[]}
                            currentPath={breadcrumbCurrentPath}
                            onNavigateBack={goToJobs}
                            onNavigateToPath={goToJobs}
                        />
                    </div>
 
                    {loadError && (
                        <div
                            style={{
                                ...card,
                                borderColor: token.color.danger,
                                backgroundColor: token.color.dangerTint,
                                color: token.color.danger,
                                padding: '12px 16px',
                                fontSize: '13px',
                                fontWeight: 600,
                                marginBottom: '18px'
                            }}
                        >
                           Values shown below may be out of date or unavailable — use Refresh to try again.
                        </div>
                    )}
 
                    {/* Title row */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ fontSize: '22px', fontWeight: 700, color: token.color.textPrimary, fontFamily: token.font.mono }}>
                                    {job.id}
                                </span>
                                <span style={{ marginLeft: '10px', verticalAlign: 'middle' }}>
                                    <EDSBadge
                                        variant={getJobStatusBadge(job.status).variant}
                                        value={getJobStatusBadge(job.status).value}
                                        label=""
                                        size="medium"
                                    />
                                </span>
                                {isLoading && (
                                    <span style={{ fontSize: '12px', color: token.color.textMuted, marginLeft: '10px' }}>Loading...</span>
                                )}
                            </div>
                            <div style={{ fontSize: '13.5px', color: token.color.textSecondary, marginTop: '4px' }}>
                                {job.sourceBucket} &middot; {formatDate(job.createdAt)}
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <EDSButton
                                variant="tertiary"
                                type="button"
                                label={isCancellingJob ? 'Cancelling...' : 'Cancel'}
                                onEDSButtonClick={cancelJob}
                                isDisabled={isCancellingJob || (!isActiveJob && !isRetryingJob)}
                            />
                            <EDSButton
                                variant="secondary"
                                type="button"
                                label={isRetryingJob ? 'Retrying...' : 'Retry'}
                                onEDSButtonClick={retryJob}
                                isDisabled={isRetryingJob || !isRetryableJob || isCancellingJob}
                            />
                            <EDSButton
                                variant="primary"
                                type="button"
                                label={isRefreshing ? 'Refreshing...' : 'Refresh'}
                                onEDSButtonClick={refreshJobDetails}
                                isDisabled={isRefreshing || isRetryingJob || isCompletedJob}
                            />
                        </div>
                    </div>
 
                    {/* Progress card */}
                    <div style={{ ...card, marginBottom: '20px' }}>
                        <div style={cardHeader}>
                            <span style={{ fontSize: '15px', fontWeight: 700, color: token.color.textPrimary }}>Progress</span>
                        </div>
                        <div style={{ padding: '24px', backgroundColor: token.color.surfaceMuted, borderBottomLeftRadius: token.radius.lg, borderBottomRightRadius: token.radius.lg }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '24px', marginBottom: '22px' }}>
                                <BadgeStatusKeyValuePair label="Total" value={job.total} variant="accent-gray-number" />
                                <BadgeStatusKeyValuePair label="Copied" value={job.copied} variant="success" />
                                <BadgeStatusKeyValuePair label="Failed" value={failedTotal} variant="alert-high" />
                            </div>
 
                            <div style={{ marginTop: '4px' }}>
                                <EDSProgressIndicator
                                    variant="linear"
                                    value={copiedPct}
                                    max={100}
                                    status="active"
                                    labelPrimary={`Copied ${job.copied} of ${job.total}`}
                                    labelSecondary={`Failed ${failedTotal} (${failedPct}%)`}
                                    style={{ '--eds-misc-gradient-linear-progress': 'linear-gradient(90deg, #227735 0%, #2f8f44 100%)' }}
                                />
                            </div>
                        </div>
                    </div>
 
                    {showFailureReason && (
                        <div
                            style={{
                                ...card,
                                borderColor: token.color.danger,
                                backgroundColor: token.color.dangerTint,
                                color: token.color.danger,
                                padding: '12px 16px',
                                fontSize: '13px',
                                fontWeight: 600,
                                marginBottom: '18px'
                            }}
                        >
                            Failure reason: {failureReasonText}
                            {nextSteps.length > 0 && (
                                <div style={{ marginTop: '8px' }}>
                                    <div>Next steps:</div>
                                    <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                                        {nextSteps.map((step, index) => (
                                            <li key={`next-step-${index}`} style={{ marginBottom: '4px' }}>
                                                {step}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
 
                </div>
            </div>
        </div>
    );
};
 
export default JobDetailsPage;
 